import {
  DueDiligenceItem,
  IDueDiligenceItem,
} from '@app/models/DueDiligenceItem'
import { ICurrentUserState } from '@app/store/modules/account'
import { bindFunctions } from '@app/utils/class'
import { formatNumber } from '@app/utils/formatting'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { Button, Collapse, Progress } from 'reactstrap'
import './DevelopmentPipeline.scss'
import {
  DevelopmentPipelineItem,
  IPipelineItem,
} from './DevelopmentPipelineItem'

interface IDevelopmentPipelineProps {
  addItem: () => void
  className?: string
  deleteItem: (id: string) => void
  editItem: (item: DueDiligenceItem) => void
  items: IDueDiligenceItem[]
  isCurrent?: boolean
  style?: React.CSSProperties
  title: string
}

type DevelopmentPipelineProps = IDevelopmentPipelineProps & ICurrentUserState

interface IDevelopmentPipelineState {
  isOpen: boolean
  items: IPipelineItem[]
  percentComplete: number
}

export default class DevelopmentPipeline extends React.PureComponent<
  DevelopmentPipelineProps,
  IDevelopmentPipelineState
> {
  constructor(props: DevelopmentPipelineProps) {
    super(props)
    this.state = {
      isOpen: props.isCurrent === true || false,
      items: [],
      percentComplete: 0,
    }
    bindFunctions(this)
  }

  public componentDidMount() {
    this._buildPipeline()
  }

  public componentDidUpdate(prevProps: DevelopmentPipelineProps) {
    if (this.props.items !== prevProps.items) {
      this._buildPipeline()
    }
    if (
      this.props.isCurrent === true &&
      this.props.isCurrent !== prevProps.isCurrent
    ) {
      this.setState({ isOpen: true })
    }
  }

  public render() {
    const toggleIcon: IconName = this.state.isOpen
      ? 'chevron-down'
      : 'chevron-right'

    return (
      <div className={this.props.className} style={this.props.style}>
        <div className='development-pipeline'>
          <h5 className='cursor-pointer flex-grow-1' onClick={this._toggle}>
            <FontAwesomeIcon icon={['far', toggleIcon]} fixedWidth />{' '}
            {this.props.title}
          </h5>
          {this.state.isOpen && this._canAddItem() && (
            <div>
              <Button
                className='mr-2'
                color='primary'
                onClick={this.props.addItem}
                size='sm'
              >
                <FontAwesomeIcon icon={['fal', 'plus']} fixedWidth /> Add Item
              </Button>
            </div>
          )}
          <div className='development-pipeline-progress'>
            <div className='mr-2'>
              {formatNumber(this.state.percentComplete, 0)}%
            </div>
            <Progress color='success' value={this.state.percentComplete} />
          </div>
        </div>
        <Collapse isOpen={this.state.isOpen}>
          {this.state.items.map((item) => (
            <DevelopmentPipelineItem
              key={item.item.id}
              deleteItem={this.props.deleteItem}
              editItem={this.props.editItem}
              item={item}
            />
          ))}
        </Collapse>
      </div>
    )
  }

  private _buildPipeline() {
    const items = this.props.items
      .filter((i) => !i.parentKey)
      .map(this._getItemChildren)
    const publicCount = this.props.items.filter((i) => i.isPublic).length
    const completeCount = this.props.items.filter(
      (i) => i.isComplete && i.isPublic,
    ).length

    this.setState({
      items,
      percentComplete: (completeCount / publicCount) * 100,
    })
  }

  private _canAddItem(): boolean {
    return (
      this.props.user !== undefined && !this.props.user.permissions.isGuest
    )
  }

  private _getItemChildren(item: IDueDiligenceItem): IPipelineItem {
    return {
      children: this.props.items
        .filter((i) => i.parentKey === item.key)
        .map(this._getItemChildren),
      item,
    }
  }

  private _toggle() {
    this.setState({ isOpen: !this.state.isOpen })
  }
}
